import React, { useEffect, useState } from "react";
import { db } from "../firebase/firebaseConfig";
import { collection, onSnapshot, query, orderBy } from "firebase/firestore";
import dayjs from "dayjs";
import "dayjs/locale/cs";
import { motion } from "framer-motion";

dayjs.locale("cs");

const EventDisplay = () => {
  const [events, setEvents] = useState([]);
  const [userMap, setUserMap] = useState({});

  useEffect(() => {
    const q = query(collection(db, "events"), orderBy("startTime"));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setEvents(data);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
      const users = {};
      snapshot.docs.forEach((doc) => {
        const { email, name } = doc.data();
        if (email && name) users[email] = name;
      });
      setUserMap(users);
    });
    return () => unsubscribe();
  }, []);

  const now = dayjs();
  const todayStart = now.startOf("day");
  const todayEnd = now.endOf("day");

  const todayEvents = events.filter((event) => {
    const start = dayjs(event.startTime.seconds * 1000);
    return start.isAfter(todayStart) && start.isBefore(todayEnd);
  });

  const currentEvent = todayEvents.find((e) => dayjs(e.startTime.seconds * 1000).isBefore(now) && dayjs(e.endTime.seconds * 1000).isAfter(now));

  const upcomingToday = todayEvents.filter((e) => dayjs(e.startTime.seconds * 1000).isAfter(now) && (!currentEvent || e.id !== currentEvent.id));

  const getOrganizerName = (email) => userMap[email] || email || "Neznámý pořadatel";

  return (
    <div className="min-h-screen bg-black text-white flex flex-col items-center justify-center px-4 py-8 space-y-6">
      <div className="text-center space-y-4 max-w-2xl">
        <h2 className="text-green-400 text-2xl font-semibold">{dayjs().format("dddd, D.M.YYYY")}</h2>

        {currentEvent ? (
          <>
            <h1 className="text-green-400 text-5xl font-bold">
              {dayjs(currentEvent.startTime.seconds * 1000).format("HH:mm")} – {dayjs(currentEvent.endTime.seconds * 1000).format("HH:mm")}
            </h1>
            <hr className="border-gray-300 my-3" />
            <h2 className="text-white text-4xl font-extrabold mt-4">{currentEvent.eventType}</h2>
            <p className="text-md leading-relaxed mt-2">
              Pořádá <strong>{getOrganizerName(currentEvent.organizer)}</strong>
              {currentEvent.description ? `, ${currentEvent.description}` : ""}
            </p>
            <hr className="border-gray-300 my-6" />
          </>
        ) : (
          <>
            <h1 className="text-gray-400 text-3xl font-semibold mt-4">Aktuálně neprobíhá žádná událost v zasedací místnosti.</h1>
            <hr className="border-gray-300 my-6" />
          </>
        )}

        {upcomingToday.length > 0 && (
          <div className="mt-6 space-y-6 text-gray-400 max-w-2xl mx-auto">
            <h3 className="text-green-400 text-3xl font-bold">Další dnešní události</h3>
            {upcomingToday.slice(0, 2).map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 80, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -50, scale: 0.9 }}
                transition={{ duration: 1.0, delay: index * 0.2 }} // zpomaleno a větší mezery mezi položkami
                className="text-center border border-gray-700 rounded-lg p-4 bg-gray-900"
              >
                <p className="text-lg font-semibold text-white">
                  {dayjs(event.startTime.seconds * 1000).format("HH:mm")} – {dayjs(event.endTime.seconds * 1000).format("HH:mm")} • {event.eventType}
                </p>
                <p className="text-sm mt-1 text-gray-300">
                  Pořádá <strong>{getOrganizerName(event.organizer)}</strong>
                </p>
                {event.description && <p className="text-sm text-gray-400 mt-1 italic">{event.description}</p>}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default EventDisplay;
